import styled from 'styled-components'

const mono =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0.75rem 0 2rem;
`

export const ListToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
`

export const FilterGroup = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`

export const FilterButton = styled.button`
  margin: 0;
  padding: 0.3rem 0.65rem;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ $active, theme }) => {
    if (!$active) return 'transparent'
    return theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.14)'
      : 'rgba(62, 207, 142, 0.1)'
  }};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : theme.colors.textSecondary};
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:last-child {
    border-right: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: -2px;
  }
`

export const CountLabel = styled.span`
  font-size: 0.65rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`

export const ErrorText = styled.p`
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.action.danger.bg}33;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(220, 38, 38, 0.1)'
      : 'rgba(220, 38, 38, 0.05)'};
  color: ${({ theme }) => theme.colors.action.danger.bg};
  font-size: 0.75rem;
  font-weight: 500;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 1.25rem 1rem;
  border-radius: 6px;
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};

  strong {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    font-size: 0.6875rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

export const LevelBadge = styled.span`
  display: inline-block;
  font-family: ${mono};
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ $tone, theme }) =>
    $tone === 'error'
      ? theme.colors.action.danger.bg
      : theme.colors.textMuted};
`

export const MessageCell = styled.span`
  display: block;
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const TimeCell = styled.span`
  display: block;
  white-space: nowrap;
  font-family: ${mono};
  font-size: 0.65rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const MetaRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  min-width: 200px;
  max-width: 560px;
`

export const MetaEmpty = styled.span`
  font-family: ${mono};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.65rem;
`

export const MetaKv = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${mono};
  font-size: 0.65rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const MetaKey = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
`

export const MetaVal = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const MetaSep = styled.span`
  color: ${({ theme }) => theme.colors.borderStrong};
  margin: 0 0.35rem;
`

export const DetailOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(15, 23, 42, 0.28)'};
  z-index: 1000;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 220ms ease;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const DetailPanel = styled.aside`
  display: flex;
  flex-direction: column;
  width: min(100%, 420px);
  height: 100%;
  background: ${({ theme }) =>
    theme.name === 'dark' ? theme.colors.backgroundSecondary : theme.colors.background};
  border-left: 1px solid ${({ theme }) => theme.colors.borderDefault};
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.12);
  transform: translate3d(${({ $open }) => ($open ? '0' : '100%')}, 0, 0);
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const DetailHeader = styled.header`
  flex-shrink: 0;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`

export const DetailTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

export const DetailTitle = styled.h2`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const DetailClose = styled.button`
  margin: 0;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const DetailMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-top: 0.55rem;
  font-family: ${mono};
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const DetailBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0.85rem 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
`

export const DetailSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

export const DetailSectionTitle = styled.h3`
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const DetailMessage = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;
`

export const DetailKvList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 0.65rem;
  font-family: ${mono};
  font-size: 0.65rem;
`

export const DetailKvItem = styled.div`
  display: contents;
`

export const DetailKvKey = styled.dt`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const DetailKvValue = styled.dd`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const DetailActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`

export const DetailActionButton = styled.button`
  margin: 0;
  padding: 0.2rem 0.5rem;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? `${theme.colors.action.primary.bg}66`
        : theme.colors.borderDefault};
  border-radius: 4px;
  background: ${({ $active, theme }) => {
    if (!$active) return 'transparent'
    return theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.14)'
      : 'rgba(62, 207, 142, 0.1)'
  }};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : theme.colors.textSecondary};
  font-family: ${mono};
  font-size: 0.6rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const DetailJson = styled.div`
  max-height: min(45vh, 360px);
  overflow: auto;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(0,0,0,0.28)' : theme.colors.borderSubtle};
  font-family: ${mono};
  font-size: 0.625rem;

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
`

export const DetailStack = styled.pre`
  margin: 0;
  max-height: min(35vh, 280px);
  overflow: auto;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(0,0,0,0.28)' : theme.colors.borderSubtle};
  font-family: ${mono};
  font-size: 0.6rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textSecondary};

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
`

export const DetailEmpty = styled.span`
  font-family: ${mono};
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
`
